import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { formatInTimeZone } from 'date-fns-tz';
import { useTenantApi } from '@/hooks';
import { useBetterNavigate } from '@/utils';
import className from 'classnames';
import { ByzzerTable } from '@/components/ByzzerTable';
import { ByzzerMenu } from '@/components/ByzzerMenu';
import { ByzColDef } from '@/types/TableTypes';
import { SimulatorRunSummary } from '@/types/SimulatorHistoryTypes';
import { FLOATING_DATE_PICKER_FILTER_PARAMS } from '@/constants/table.constants';
import { confirm, alert as ByzzerAlert } from '@/components/form/ByzzerModal';
import { openModal } from '@/components/form';
import { ByzzerMask } from '@/components/ByzzerMask/ByzzerMask';
import { TimePeriod, WeekEndingTimePeriod, MonthEndingTimePeriod } from '@/types/ReportRun';
import DownloadIcon from '@images/icons/DownloadIcon.svg';
import ReplayIcon from '@images/icons/ReplayIcon.svg';
import { ByzzerTextInput } from '@byzzer/ui-components';
import './SimulatorHistoryPage.scss';
import { useUser } from '@/contexts/UserContext';

function formatSimulatorTimePeriod(timePeriod: TimePeriod | null | undefined): string {
    if (!timePeriod) return '';
    if (timePeriod.type === 'week_ending') {
        const { weeks, endDate } = (timePeriod as WeekEndingTimePeriod).period ?? {};
        if (weeks && endDate) {
            const d = new Date(endDate);
            const formatted = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
            return `${weeks} Week${weeks > 1 ? 's' : ''} Ending ${formatted}`;
        }
    }
    if (timePeriod.type === 'omni_custom' || timePeriod.type === 'cps_custom') {
        const { months, endDate } = (timePeriod as MonthEndingTimePeriod).period ?? {};
        if (months && endDate) {
            const d = new Date(endDate);
            const formatted = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
            return `${months} Month${months > 1 ? 's' : ''} Ending ${formatted}`;
        }
    }
    // relative / omni / osh / cps_relative — period is a plain string
    const period = (timePeriod as any).period;
    return typeof period === 'string' ? period : '';
}

type SimulatorHistoryColId = 'simulatorName' | 'simulatorId' | 'simulatorType' | 'runDate' | 'brands' | 'categories' | 'ppgDefinition' | 'ppg' | 'market' | 'timePeriod' | 'actions';

export const SimulatorHistoryPage: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const { getSimulatorRunHistory, downloadSimulatorResultFile, deleteSimulatorHistoryById, renameSimulatorHistoryById } = useTenantApi();
    const { allProducts } = useUser();
    // AG Grid caches cell renderers on first render, so callbacks captured in cellRenderer
    // see a stale closure. A ref ensures we always read the latest allProducts at call time.
    const allProductsRef = useRef(allProducts);
    allProductsRef.current = allProducts;

    const navigate = useBetterNavigate();
    const baseClass = className('simulator-history');
    
    const [simulatorData, setSimulatorData] = useState<SimulatorRunSummary[]>([]);

    useEffect(() => {
        setLoading(true);
        getSimulatorRunHistory()
            .then((data) => setSimulatorData(data ?? []))
            .catch((err) => console.error('Failed to load simulator history', err))
            .finally(() => setLoading(false));
    }, []);
    
    const gridRef = useRef<AgGridReact>(null);
    const menuRefs = useRef<Record<string, { current: HTMLElement | null }>>({});

    const onDownload = useCallback(async (data: SimulatorRunSummary) => {
        try {
            setLoading(true);
            await downloadSimulatorResultFile(data.id);
        } catch (error) {
            console.error('Download failed', error);
        } finally {
            setLoading(false);
        }
    }, [downloadSimulatorResultFile]);

    const onRunAgain = useCallback(async (data: SimulatorRunSummary) => {
        // data.sku is the dataset SKU stored server-side (from POST /datasets.run body {sku: datasetSku}).
        // Read via ref to avoid AG Grid stale-closure issue where cell renderers capture
        // the allProducts array from first render (when it was still []).
        const allSimulatorProducts = allProductsRef.current.filter((p) => p.type === 'simulator');
        const simulatorProduct =
            allSimulatorProducts.find((p) => p.metadata?.datasetSku === data.sku) ??
            allSimulatorProducts.find((p) => p.sku === data.sku);

        if (!simulatorProduct) {
            await ByzzerAlert({
                title: 'Simulator Not Found',
                content: 'Unable to locate the simulator for this run. Please contact support.',
            });
            return;
        }

        navigate('/dashboard/configure_simulator', {
            params: {
                sku: simulatorProduct.sku,
                datasetSku: data.sku,
                runId: data.id,
                mode: 'edit',
            },
            state: {
                runConfig: {
                    categoryNames: data.categories,
                    brandNames: data.brands,
                    ppgId: data.ppgId,
                    ppgDefinitionName: data.ppgDefinition,
                    ppgGroupName: data.ppg,
                    marketNames: data.marketNames ?? [],
                    marketKeys: data.marketKeys ?? [],
                    timePeriod: data.timePeriod,
                },
            },
        });
    }, [navigate]);

    const onDelete = useCallback(async (data: SimulatorRunSummary) => {
        const confirmed = await confirm({
            title: 'Delete Simulation',
            content: <div className="byzzer-allocation-warning">Do you want to delete the selected item?</div>,
            yesLabel: 'Yes',
            noLabel: 'No',
        });

        if (confirmed) {
            try {
                setLoading(true);
                await deleteSimulatorHistoryById(data.id);
                setSimulatorData((prev) => prev.filter((row) => row.id !== data.id));
            } catch (error) {
                await ByzzerAlert({
                    title: 'Delete Failed',
                    content: 'Unable to delete the simulation. Please try again.',
                });
            } finally {
                setLoading(false);
            }
        }
    }, [deleteSimulatorHistoryById]);

    const onRename = useCallback(async (data: SimulatorRunSummary) => {
        try {
            const updatedName = await openModal({
                className: 'simulator-history-rename-modal',
                title: 'Rename',
                initialState: {
                    id: data.id,
                    name: data.simulatorName,
                },
                showCloseOption: false,
                content: ({ busy, state, setState }) => {
                    return (
                        <>
                            <ByzzerMask loading={busy}>Renaming your simulation</ByzzerMask>
                            <ByzzerTextInput
                                className="simulator-history-rename-modal__name"
                                name={'simulationName'}
                                placeholder={'Enter a title'}
                                value={state.name}
                                onChange={(e) => {
                                    setState((prev) => ({
                                        ...prev,
                                        name: e.value,
                                    }));
                                }}
                            />
                        </>
                    );
                },
                actions: [
                    {
                        key: 'cancel',
                        type: 'negative',
                        label: 'Cancel',
                        disableIf({ busy }) {
                            return busy;
                        },
                        action({ resolve }) {
                            resolve();
                        },
                    },
                    {
                        key: 'update',
                        label: 'Update',
                        getDisableTip({ state: { name } }) {
                            return !name?.trim() ? 'Name is required' : '';
                        },
                        disableIf({ busy, state: { name } }) {
                            return busy || !name?.trim();
                        },
                        async action({ resolve, reject, setBusy, state }) {
                            try {
                                setBusy(true);
                                const trimmedName = state.name.trim();
                                await renameSimulatorHistoryById(state.id, trimmedName);
                                resolve(trimmedName);
                            } catch (error) {
                                reject(error);
                            } finally {
                                setBusy(false);
                            }
                        },
                    },
                ],
            });

            if (typeof updatedName === 'string' && updatedName.trim()) {
                setSimulatorData((prev) =>
                    prev.map((row) =>
                        row.id === data.id
                            ? { ...row, simulatorName: updatedName.trim() }
                            : row
                    )
                );
            }
        } catch (error) {
            await ByzzerAlert({
                title: 'Rename Failed',
                content: 'Unable to rename the simulation. Please try again.',
            });
        }
    }, [renameSimulatorHistoryById]);

    const defaultColDef = useMemo(
        () => ({
            filter: true,
            sortable: true,
            floatingFilter: true,
            resizable: true,
            width: 180,
            suppressMenu: true,
            flex: 0,
            autoHeight: false,
            wrapText: false,      
            lockVisible: true 
        }),
        []
    );

    // Use refs so columnDefs stays a stable reference and doesn't invalidate
    // ByzzerTable's recalculateCheckBoxVisibility when user context updates after layout save.
    const onDownloadRef = useRef(onDownload);
    onDownloadRef.current = onDownload;
    const onRunAgainRef = useRef(onRunAgain);
    onRunAgainRef.current = onRunAgain;
    const onDeleteRef = useRef(onDelete);
    onDeleteRef.current = onDelete;
    const onRenameRef = useRef(onRename);
    onRenameRef.current = onRename;

    const columnDefs = useMemo<ByzColDef<SimulatorRunSummary, SimulatorHistoryColId>[]>(() => [
        {
            colId: 'simulatorName',
            headerName: 'Simulation Name',
            width: 250,
            valueGetter: ({ data }) => data.simulatorName,
            tooltipValueGetter: ({ data }) => data.simulatorName,
            pinned: 'left',
        },
        {
            colId: 'simulatorId',
            headerName: 'Simulation ID',
            hide: true,
            valueGetter: ({ data }) => data.id,
        },
        {
            colId: 'simulatorType',
            headerName: 'Simulation Type',
            width: 200,
            valueGetter: ({ data }) => data.simulatorType,
        },
        {
            ...FLOATING_DATE_PICKER_FILTER_PARAMS,
            headerName: 'Run Date',
            colId: 'runDate',
            field: 'runDate',
            valueGetter: ({ data }) => data.runDate
                ? formatInTimeZone(new Date(data.runDate), 'America/New_York', 'yyyy-MM-dd')
                : '',
        },
        {
            colId: 'brands',
            headerName: 'Brand(s)',
            valueGetter: ({ data }) => data.brands?.join(', ') ?? '',
            tooltipValueGetter: ({ data }) => data.brands?.join(', ') ?? '',
        },
        {
            colId: 'categories',
            headerName: 'Category(s)',
            valueGetter: ({ data }) => data.categories?.join(', ') ?? '',
            tooltipValueGetter: ({ data }) => data.categories?.join(', ') ?? '',
        },
        {
            colId: 'ppgDefinition',
            headerName: 'PPG Definition',
            valueGetter: ({ data }) => data.ppgDefinition,
        },
        {
            colId: 'ppg',
            headerName: 'PPG',
            width: 300,
            valueGetter: ({ data }) => data.ppg,
            tooltipValueGetter: ({ data }) => data.ppg,
        },
        {
            colId: 'market',
            headerName: 'Market',
            valueGetter: ({ data }) => data.marketNames?.join(', ') ?? '',
        },
        {
            colId: 'timePeriod',
            headerName: 'Time Period',
            valueGetter: ({ data }) => formatSimulatorTimePeriod(data.timePeriod),
            tooltipValueGetter: ({ data }) => formatSimulatorTimePeriod(data.timePeriod),
        },
        {
            headerName: 'Actions',
            colId: 'actions',
            width: 120,
            pinned: 'right',
            sortable: false,
            filter: false,
            cellStyle: { display: 'flex', alignItems: 'center' },
            cellRenderer: ({ data }) => (
                <div className="actions-cell">
                    <button
                        type="button"
                        aria-label={data.hasFile ? 'Download' : 'File not available'}
                        title={data.hasFile ? 'Download' : 'File not available'}
                        disabled={!data.hasFile}
                        onClick={() => onDownloadRef.current(data)}
                        className="actions-cell__btn"
                    >
                        <img src={DownloadIcon} className={`actions-cell__icon${!data.hasFile ? ' actions-cell__icon--disabled' : ''}`} alt="" />
                    </button>
                    <button
                        type="button"
                        aria-label="Run Again"
                        title="Run Again"
                        onClick={() => onRunAgainRef.current(data)}
                        className="actions-cell__btn"
                    >
                        <img src={ReplayIcon} className="actions-cell__icon" alt="" />
                    </button>
                    <div className="actions-cell__menu-wrap">
                        <button
                            type="button"
                            aria-label="More actions"
                            title="More actions"
                            className="actions-cell__menu-trigger"
                            ref={(element) => {
                                if (!menuRefs.current[String(data.id)]) {
                                    menuRefs.current[String(data.id)] = { current: null };
                                }
                                menuRefs.current[String(data.id)].current = element;
                            }}
                        />
                        <ByzzerMenu
                            className="actions-cell__menu"
                            reference={menuRefs.current[String(data.id)]}
                            offset={[-70, -5]}
                            triggerTarget={menuRefs.current[String(data.id)]?.current}
                            highlightItemOnHover={true}
                            items={[
                                {
                                    onClick: () => onDeleteRef.current(data),
                                    content: 'Delete',
                                },
                                {
                                    onClick: () => onRenameRef.current(data),
                                    content: 'Rename',
                                },
                            ]}
                        />
                    </div>
                </div>
            )
        }
    ], []);

    return (
        <div className={baseClass}>
            <ByzzerMask show={loading} loading={loading}>
                Loading Simulation History
            </ByzzerMask>
            <ByzzerTable
                ref={gridRef}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowData={simulatorData}
                tableArea={'simulatorHistory'}
                enableColumnHideShow={true}
                enableSaveLayout={true}
                enableSaveSort={true}
                uncheckedColumns={['simulatorId']}
            />
        </div>
    );
};

export default SimulatorHistoryPage;

